import { type MouseEvent, useEffect, useReducer } from 'react';
import { type AxiosError } from 'axios';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { type ClickType } from '@jsmdg/tracking';
import { DiscountType, ProductType } from '../../shared/enums';
import {
    isBox,
    isExperience,
    isValueVoucher,
    isValueVoucherWithPriceRange,
} from '../../shared/product-safeguards';
import { type Location, type Money } from '../../shared/types';
import {
    trackAddToCartClicked,
    trackEECAddToCartAdded,
    trackEECProductDetailViewed,
} from '../components/BuyboxContainer/buyboxContainer.tracking';
import { sendAlgoliaProductAddedEvent } from '../helper/algoliaAnalytics';
import { dispatchEvent } from '../helper/EventUtils';
import { getPurchaseType } from '../helper/getPurchaseType';
import {
    dispatchClusterType,
    dispatchPurchaseType,
    dispatchResetErrors,
    dispatchSeatCountChange,
    dispatchValueVoucherPriceChange,
    handleErrorByCode,
} from '../reducers/actions/buyboxReducerActions';
import { buyboxReducer } from '../reducers/buyboxReducer';
import { eventTypes } from '../types';
import { ClusterType } from '../types/enums/clusterType';
import { PurchaseType } from '../types/enums/purchaseType';
import { BuyboxReducerActionType } from '../types/enums/reducer/buyboxReducerAction';
import { SellOption } from '../types/enums/sellOption';
import { type ProductDetailContext } from '../types/types';
import { useInsightsClient } from './useInsightsClient';
import { useProduct } from './useProduct';
import { useRedemptionCart } from './useRedemptionCart';

type UseBuyboxReturn = {
    purchaseType?: PurchaseType;
    clusterType?: ClusterType;
    bookableLocationsCount: number;
    locations: Location[];
    seatCount?: string;
    handleValueVoucherPriceChange: (value: Money) => void;
    isAddToCartOrRedemptionDisabled: (type: PurchaseType | undefined) => boolean;
    hasFriendsDiscount: boolean;
    isProductPaybackCountable: boolean;
    hasCookieWarning?: boolean;
    hasMixedShoppingCartError?: boolean;
    hasAddingMultipleItemsError?: boolean;
    hasAddToCartError?: boolean;
    ticketPurchaseIsSelected: (type: PurchaseType | undefined) => boolean;
    voucherClusterIsSelected: (type: ClusterType | undefined) => boolean;
    handlePurchaseType: (value: PurchaseType) => void;
    handleClusterType: (value: ClusterType) => void;
    handleSeatCountChange: (newSeatCount: string, trackSelection?: boolean) => Promise<void>;
    handleAddToCartClick: (
        event?: MouseEvent<HTMLButtonElement>,
        clusterProductId?: string,
        clickType?: ClickType,
    ) => Promise<void>;
    handleModalClose: () => void;
    handleGotoExperiences: ({
        event,
        clickType,
        clickText,
    }: {
        event?: MouseEvent<HTMLButtonElement>;
        clickType?: ClickType;
        clickText: string;
    }) => void;
};

const isVoucherPurchase = (typeOfPurchase: PurchaseType | undefined) =>
    typeOfPurchase === PurchaseType.Voucher;

const isVoucherClusterSelected = (clusterType: ClusterType | undefined) =>
    clusterType === ClusterType.FLEXIBLE_VOUCHER;

const useBuybox = (): UseBuyboxReturn => {
    const { insightsClient } = useInsightsClient();

    const product = useProduct();
    const {
        addItemToCart,
        createAddToCartButtonClickedEvent,
        redemptionInformation,
        tenantConfig,
        isBoxProductWithQrCodeLink,
    }: ProductDetailContext = useFragmentContext();
    const { isRedemptionCartFilled } = useRedemptionCart();

    const {
        productId,
        manufacturerSku,
        trackingPath,
        price,
        originalPrice,
        title,
        discountBadge,
        discountType,
        inPreview,
        sellable,
        type,
    } = product;

    const hasFriendsDiscount = discountType === DiscountType.FriendDiscount;

    const { locations, sellOption, minParticipants, maxParticipants } = isExperience(product)
        ? {
              locations: product.locations,
              sellOption: product.sellOption,
              minParticipants: product.minParticipants,
              maxParticipants: product.maxParticipants,
          }
        : {
              locations: [],
              sellOption: undefined,
              minParticipants: undefined,
              maxParticipants: undefined,
          };

    const bookableLocationsCount = locations.filter(
        (bookableLocation: Location) => bookableLocation.activeAppointments,
    ).length;

    const [
        {
            purchaseType,
            seatCount,
            hasCookieWarning,
            hasMixedShoppingCartError,
            hasAddingMultipleItemsError,
            hasAddToCartError,
            clusterType,
            valueVoucherPrice,
        },
        dispatchBuybox,
    ] = useReducer(buyboxReducer, {
        purchaseType: getPurchaseType(!!redemptionInformation, sellOption),
        clusterType: ClusterType.PRODUCT_VOUCHER,
        valueVoucherPrice: isValueVoucherWithPriceRange(product)
            ? product.priceRange?.min
            : undefined,
    });

    const quantity = Number(seatCount || 1);

    const handleGotoExperiences = ({
        event,
        clickType,
        clickText,
    }: {
        event?: MouseEvent<HTMLButtonElement>;
        clickType?: ClickType;
        clickText: string;
    }): void => {
        event?.preventDefault();
        trackAddToCartClicked(clickType, clickText);
        window.location.assign(`/experiences/${productId}`);
    };

    const handleAddToCartClick = async (
        event?: MouseEvent<HTMLButtonElement>,
        clusterProductId?: string,
        clickType?: ClickType,
    ): Promise<void> => {
        event?.preventDefault();
        if (!navigator?.cookieEnabled) {
            dispatchBuybox({ type: BuyboxReducerActionType.CookieWarning });
            return;
        }

        // trigger AJAX req early
        const promise = addItemToCart({
            productId: clusterProductId ?? productId,
            quantity,
            valueVoucherPrice,
        });

        await window.yieldToMainThread();

        if (!clusterProductId) {
            trackEECAddToCartAdded(product, locations, quantity, hasFriendsDiscount, purchaseType);
        }

        trackAddToCartClicked(clickType);

        if (insightsClient) {
            const trackingPrice = Number(price.gross.amount);
            const trackingOriginalPrice = Number(originalPrice?.gross.amount);

            sendAlgoliaProductAddedEvent({
                insightsClient,
                productId,
                objectData: [
                    {
                        price: trackingPrice,
                        quantity,
                        discount: originalPrice
                            ? Number((trackingOriginalPrice - trackingPrice).toFixed(2))
                            : undefined,
                    },
                ],
                value: Number((trackingPrice * quantity).toFixed(2)),
                currency: price.gross.currencyCode,
            });
        }

        try {
            const { cartId } = await promise;

            await createAddToCartButtonClickedEvent({ productId, cartId });
            dispatchEvent(eventTypes.NAVIGATE_CART);
        } catch (error) {
            const response: { errorCode?: number } | unknown = (error as AxiosError).response?.data;
            handleErrorByCode(dispatchBuybox, (response as { errorCode?: number })?.errorCode);
        }
    };

    const ticketPurchaseIsSelected = (
        typeOfPurchase: PurchaseType | undefined,
        // eslint-disable-next-line unicorn/consistent-function-scoping
    ): boolean => {
        return typeOfPurchase === PurchaseType.Ticket;
    };

    useEffect(() => {
        trackEECProductDetailViewed(
            product,
            locations,
            purchaseType,
            minParticipants,
            maxParticipants,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackingPath, manufacturerSku, price, originalPrice, title, discountBadge?.id]);

    return {
        handleGotoExperiences,
        purchaseType,
        clusterType,
        locations,
        seatCount,
        isProductPaybackCountable:
            tenantConfig.paybackIsEnabled &&
            !isValueVoucher(product) &&
            !tenantConfig.nonPaybackSkus.includes(manufacturerSku),
        isAddToCartOrRedemptionDisabled: (typeOfPurchase): boolean => {
            if (
                !bookableLocationsCount &&
                ProductType.EXPERIENCE === type &&
                sellOption === SellOption.TicketOnly
            ) {
                return true;
            }

            const isNotSellableAndInTheShoppingFlow = !sellable && !redemptionInformation;

            const isNotSellableAndIsVoucherTypeSelected =
                !sellable && isVoucherPurchase(typeOfPurchase);

            const isInRedemptionFlowAndABoxOrVoucher = Boolean(
                redemptionInformation &&
                    (isBox(product) ||
                        isValueVoucher(product) ||
                        isVoucherPurchase(typeOfPurchase)),
            );

            const shouldAlwaysDisableAddToCart =
                isRedemptionCartFilled ||
                inPreview ||
                isBoxProductWithQrCodeLink ||
                isNotSellableAndInTheShoppingFlow ||
                isNotSellableAndIsVoucherTypeSelected ||
                isInRedemptionFlowAndABoxOrVoucher;

            return shouldAlwaysDisableAddToCart;
        },
        bookableLocationsCount,
        hasCookieWarning,
        hasMixedShoppingCartError,
        hasAddingMultipleItemsError,
        hasAddToCartError,
        hasFriendsDiscount,
        handlePurchaseType: (value: PurchaseType) => dispatchPurchaseType(dispatchBuybox, value),
        handleClusterType: (value: ClusterType) => dispatchClusterType(dispatchBuybox, value),
        handleValueVoucherPriceChange: (value: Money) =>
            dispatchValueVoucherPriceChange(dispatchBuybox, value),
        handleSeatCountChange: async (newSeatCount, trackSelection) =>
            dispatchSeatCountChange(dispatchBuybox, newSeatCount, title, seatCount, trackSelection),
        handleAddToCartClick,
        handleModalClose: () => dispatchResetErrors(dispatchBuybox),
        ticketPurchaseIsSelected: typeOfPurchase => ticketPurchaseIsSelected(typeOfPurchase),
        voucherClusterIsSelected: () => isVoucherClusterSelected(clusterType),
    };
};

export { useBuybox };
export type { UseBuyboxReturn };
